import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConfirmDialogService, ConfirmDialogData } from '../../services/confirm-dialog.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="dialog-overlay" *ngIf="currentDialog" (click)="onCancel()">
      <div class="dialog-container" (click)="$event.stopPropagation()">
        <div class="dialog-header" [ngClass]="'header-' + currentDialog.type">
          <i [class]="getIconClass(currentDialog.type)"></i>
          <h3>{{ currentDialog.title }}</h3>
        </div>
        <div class="dialog-body">
          <p>{{ currentDialog.message }}</p>
        </div>
        <div class="dialog-footer">
          <button class="btn-cancel" (click)="onCancel()">
            {{ currentDialog.cancelText }}
          </button>
          <button class="btn-confirm" 
                  [ngClass]="'btn-' + currentDialog.type"
                  (click)="onConfirm()">
            {{ currentDialog.confirmText }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .dialog-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      animation: fadeIn 0.2s ease-out;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .dialog-container {
      background: white;
      border-radius: 16px;
      width: 90%;
      max-width: 400px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
      animation: slideUp 0.3s ease-out;
    }

    @keyframes slideUp {
      from {
        transform: translateY(50px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    .dialog-header {
      padding: 24px;
      display: flex;
      align-items: center;
      gap: 12px;
      border-bottom: 1px solid #f0f0f0;
    }

    .dialog-header i {
      font-size: 24px;
    }

    .header-danger i {
      color: #dc3545;
    }

    .header-warning i {
      color: #ffc107;
    }

    .header-info i {
      color: #17a2b8;
    }

    .dialog-header h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #333;
    }

    .dialog-body {
      padding: 24px;
    }

    .dialog-body p {
      margin: 0;
      font-size: 14px;
      color: #666;
      line-height: 1.6;
    }

    .dialog-footer {
      padding: 16px 24px 24px;
      display: flex;
      gap: 12px;
      justify-content: flex-end;
    }

    .btn-cancel,
    .btn-confirm {
      padding: 10px 24px;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
    }

    .btn-cancel {
      background: #f5f5f5;
      color: #666;
    }

    .btn-cancel:hover {
      background: #e0e0e0;
    }

    .btn-confirm {
      color: white;
    }

    .btn-danger {
      background: #dc3545;
    }

    .btn-danger:hover {
      background: #c82333;
    }

    .btn-warning {
      background: #ffc107;
      color: #333;
    }

    .btn-warning:hover {
      background: #e0a800;
    }

    .btn-info {
      background: #17a2b8;
    }

    .btn-info:hover {
      background: #138496;
    }
  `]
})
export class ConfirmDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  currentDialog: ConfirmDialogData | null = null;

  constructor(private confirmDialogService: ConfirmDialogService) {}

  ngOnInit(): void {
    this.confirmDialogService.dialog$
      .pipe(takeUntil(this.destroy$))
      .subscribe(dialog => {
        this.currentDialog = dialog;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onConfirm(): void {
    if (this.currentDialog) {
      this.confirmDialogService.sendResult(this.currentDialog.id, true);
      this.currentDialog = null;
    }
  }

  onCancel(): void {
    if (this.currentDialog) {
      this.confirmDialogService.sendResult(this.currentDialog.id, false);
      this.currentDialog = null;
    }
  }

  getIconClass(type: string): string {
    const iconMap = {
      danger: 'fa-solid fa-exclamation-circle',
      warning: 'fa-solid fa-exclamation-triangle',
      info: 'fa-solid fa-info-circle'
    };
    return iconMap[type as keyof typeof iconMap] || iconMap.info;
  }
}
